/**
 * What an evaluation found, placed on the chips it is about: an expected source the run did not use, an
 * expected call it did not make, a call it made that was not expected, and a call made with other arguments,
 * marked on both sides.
 */

import type { DatasetExpectedTool, DatasetSourcesCheck, DatasetToolsCheck } from '@/types/simulation-dataset.types';

export interface ChipFinding {
	/** A miss, or a near miss: the right tool called with other arguments. */
	tone: 'fail' | 'warn';
	label: string;
	/** What the evaluation says about the chip, in a sentence. */
	tip: string;
	/** For a call made with other arguments, both calls, to read side by side. */
	calls?: { expected: DatasetExpectedTool; made: DatasetExpectedTool };
}

export const FINDING_CLASS: Record<ChipFinding['tone'], { border: string; text: string }> = {
	fail: { border: 'border-red-300', text: 'text-red-700' },
	warn: { border: 'border-amber-300', text: 'text-amber-700' }
};

/** Arguments compare as data, whatever order their keys were written in. */
const canonical = (value: unknown): string =>
	JSON.stringify(value, (_key, inner: unknown) =>
		inner && typeof inner === 'object' && !Array.isArray(inner)
			? Object.fromEntries(Object.entries(inner).sort(([a], [b]) => a.localeCompare(b)))
			: inner
	);

const sameCall = (a: DatasetExpectedTool, b: DatasetExpectedTool): boolean =>
	a.name === b.name && a.provider === b.provider && canonical(a.args) === canonical(b.args);

const NOT_USED: ChipFinding = { tone: 'fail', label: 'not used', tip: 'The run did not use this source.' };
const NOT_CALLED: ChipFinding = { tone: 'fail', label: 'not called', tip: 'The run did not make this call.' };
const NOT_EXPECTED: ChipFinding = { tone: 'fail', label: 'not expected', tip: 'The answer key does not expect this call.' };

/** The expected sources the run did not use, by id. */
export const sourceFindings = ({ missing }: DatasetSourcesCheck): ReadonlyMap<string, ChipFinding> =>
	new Map([...missing.sopIds, ...missing.workflowIds, ...missing.knowledgeIds].map((id) => [id, NOT_USED]));

const otherArguments = (expected: DatasetExpectedTool, made: DatasetExpectedTool, args: string[]): ChipFinding => ({
	tone: 'warn',
	label: 'other arguments',
	tip: `The run made this call with other arguments: ${args.join(', ')}.`,
	calls: { expected, made }
});

/**
 * What the check found against each call on one side, in order: on the answer key's side the calls not made
 * or made with other arguments, on the run's side the calls not expected or made with other arguments. A
 * finding marks one call, so a call listed twice is marked as often as the check found it.
 */
export const callFindings = (
	calls: DatasetExpectedTool[],
	{ missing, extra, differing }: DatasetToolsCheck,
	side: 'expected' | 'made'
): Array<ChipFinding | null> => {
	const open =
		side === 'expected'
			? [
					...missing.map((call) => ({ call, finding: NOT_CALLED })),
					...differing.map(({ expected, actual, args }) => ({ call: expected, finding: otherArguments(expected, actual, args) }))
				]
			: [
					...extra.map((call) => ({ call, finding: NOT_EXPECTED })),
					...differing.map(({ expected, actual, args }) => ({ call: actual, finding: otherArguments(expected, actual, args) }))
				];
	return calls.map((call) => {
		const index = open.findIndex((entry) => sameCall(entry.call, call));
		if (index < 0) return null;
		const [taken] = open.splice(index, 1);
		return taken?.finding ?? null;
	});
};
